/**
 * Layout del portale acquisti.
 *
 * E' l'unico pezzo di guscio che **non** si condivide con manutenzioni (ADR-0001 dice di
 * importare, non copiare, ma la navigazione e' un'altra: quattro voci contro venti, e nessuna in
 * comune). Tutto il resto — componenti UI, client API, contesto di auth — viene da @shared.
 */

import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";

const VOCI = [
  {
    to: "/contratti",
    label: "Contratti",
    icona: "📋",
    sezione: "acquisti_contratti",
    descrizione: "Archivio e censimento",
  },
  {
    to: "/capitolati",
    label: "Capitolati",
    icona: "📐",
    sezione: "acquisti_capitolati",
    descrizione: "Requisiti versionati",
  },
  {
    to: "/gare",
    label: "Gare",
    icona: "📨",
    sezione: "acquisti_gare",
    descrizione: "Richieste di offerta e offerte",
  },
  {
    to: "/analisi",
    label: "Analisi",
    icona: "📊",
    sezione: "acquisti_analisi",
    descrizione: "Comparazioni e decisioni",
  },
  {
    to: "/storico",
    label: "Storico",
    icona: "🕰️",
    sezione: "acquisti_analisi",
    descrizione: "Per tipologia e fornitore",
  },
  {
    to: "/fornitori",
    label: "Fornitori",
    icona: "🏢",
    sezione: "acquisti_fornitori",
    descrizione: "Anagrafica e riservatezza",
  },
  {
    to: "/albo",
    label: "Albo",
    icona: "🗂️",
    sezione: "acquisti_fornitori",
    descrizione: "Chi può fornire cosa",
  },
];

const PORTALE_MANUTENZIONI = "https://manutenzioni.peralbahc.com";

export default function Layout({ children }) {
  const { user, logout, canAccessSection } = useAuth();
  const navigate = useNavigate();
  const [menuAperto, setMenuAperto] = useState(false);

  const voci = VOCI.filter((v) => canAccessSection(v.sezione));

  const classeVoce = ({ isActive }) =>
    [
      "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
      isActive
        ? "bg-acquisti-700 text-white"
        : "text-gray-700 hover:bg-acquisti-50 hover:text-acquisti-800",
    ].join(" ");

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-acquisti-800 text-white shadow">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-4">
          <button
            type="button"
            className="lg:hidden text-white/80 hover:text-white text-xl leading-none"
            onClick={() => setMenuAperto((v) => !v)}
            aria-label="Apri il menu"
          >
            ☰
          </button>

          <button
            type="button"
            onClick={() => navigate("/contratti")}
            className="text-left"
          >
            <div className="font-semibold leading-tight">
              PHC · Portale Acquisti
            </div>
            <div className="text-xs text-acquisti-200">Peralba Health Care</div>
          </button>

          <div className="ml-auto flex items-center gap-4 text-sm">
            <a
              href={PORTALE_MANUTENZIONI}
              className="hidden sm:inline text-acquisti-100 hover:text-white underline underline-offset-2"
            >
              Portale manutenzioni ↗
            </a>
            <div className="hidden sm:block text-right leading-tight">
              <div className="font-medium">
                {user?.displayName || user?.email || "—"}
              </div>
              <div className="text-xs text-acquisti-200">
                {user?.ruoloLabel || "Ufficio Acquisti"}
              </div>
            </div>
            <button
              type="button"
              onClick={logout}
              className="px-3 py-1.5 rounded-md bg-acquisti-900/60 hover:bg-acquisti-900 text-sm"
            >
              Esci
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <nav
          className={[
            "w-60 shrink-0 bg-white border-r border-gray-200 p-3",
            menuAperto ? "block" : "hidden",
            "lg:block",
          ].join(" ")}
        >
          <ul className="space-y-1">
            {voci.map((v) => (
              <li key={v.to}>
                <NavLink
                  to={v.to}
                  className={classeVoce}
                  onClick={() => setMenuAperto(false)}
                >
                  <span aria-hidden="true">{v.icona}</span>
                  <span>
                    <span className="block font-medium">{v.label}</span>
                    <span className="block text-xs opacity-70">
                      {v.descrizione}
                    </span>
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>

          {voci.length === 0 && (
            <p className="text-sm text-gray-500 px-3 py-2">
              Nessuna sezione del portale acquisti è concessa al tuo ruolo.
            </p>
          )}
        </nav>

        <main className="flex-1 min-w-0 p-4 sm:p-6 overflow-x-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
